import { get, sortBy, uniq } from 'lodash';
import http from '../utils/http';
import appFeatures from '@/appFeatures';

const defaultState = {
    baseUrl: 'userInfo/',
    userToken: '', // used for user authentication
    userInfo: {},
    userScopeOptions: [],
    userPermissions: [],
    authToken: '' // used for change password workflow
};

export const getters = {
    userInitials(state) {
        let fi = state.userInfo.firstName ? state.userInfo.firstName[0] : '';
        let li = state.userInfo.lastName ? state.userInfo.lastName[0] : '';
        return (fi + li).toUpperCase();
    },
    isUserAuthenticated(state) {
        if (state.userToken) {
            return true;
        }
        return false;
    },
    isXocUser(state) {
        if (state.userInfo && state.userInfo.xocAuth) {
            return true;
        }
        return false;
    },
    isUserTokenExpired(state) {
        if (state.userToken && state.userInfo && state.userInfo.id) {
            if (state.userInfo.tokenExpires) {
                return new Date(state.userInfo.tokenExpires) <= new Date();
            } else {
                return false;
            }
        } else {
            return false;
        }
    },
    isUserMemberOfMultipleVenues(state) {
        if (state.userScopeOptions && state.userScopeOptions.length > 1) {
            return true;
        }
        return false;
    },
    distinctMsos: state => {
        if (state.userScopeOptions && state.userScopeOptions.length) {
            return sortBy(
                uniq(
                    state.userScopeOptions.map(item =>
                        item.mso.identifier.toLowerCase()
                    )
                )
            );
        }
        return [];
    }
};

export const mutations = {
    setUserToken(state, token) {
        state.userToken = token;
    },
    setUserInfo(state, userInfo) {
        state.userInfo = userInfo;
    },
    setUserPermissions(state, permissions) {
        state.userPermissions = permissions;
    },
    setUserScopeOptions(state, scopeOptions) {
        if (scopeOptions && scopeOptions.length > 0) {
            scopeOptions.sort((a, b) => {
                if (a.venue.title < b.venue.title) {
                    return -1;
                }
                if (a.venue.title > b.venue.title) {
                    return 1;
                }
                return 0;
            });
        }
        state.userScopeOptions = scopeOptions;
    },
    setAuthToken(state, token) {
        state.authToken = token;
    }
};

export const actions = {
    saveUserToken({ commit, rootGetters }, token) {
        commit('setUserToken', token);
        if (token) {
            localStorage.setItem('userToken', token);
        } else {
            localStorage.removeItem('userToken');
        }
        http.init(get(appFeatures, 'env.VMS_API'));
        http.setAuthInfo(
            token,
            rootGetters['venue/currentMso'],
            rootGetters['venue/currentPartner'],
            rootGetters['venue/currentVenue']
        );
        // ---- need to reset the app abilities
        commit('updateAppAbilities', [], { root: true });
    },
    saveAuthToken({ commit }, token) {
        commit('setAuthToken', token);
        if (token) {
            localStorage.setItem('authToken', token);
        } else {
            localStorage.removeItem('authToken');
        }
    },
    login({ commit, dispatch }, payload) {
        // will call the vms-api/login api for non-employee users
        console.log('calling /login');
        return http
            .post('login', payload, {
                suppressAuth: true,
                suppressVenue: true,
                notifyError: false,
                notifySuccess: false
            })
            .then(res => {
                commit('setUserInfo', res.data);
                return dispatch('saveUserToken', res.data.token);
            })
            .then(() => {
                // dispatch('getUserScopeData');
                return dispatch('getUserScopeOptions');
            });
    },
    convertToken({ commit, dispatch }, authCode) {
        // will convert the xerxes auth_code into a user_token
        console.log('calling convertToken: ', authCode);
        let data = { auth_code: authCode, redirect_uri: '' };
        return http
            .post('vms-api/convertToken', data, {
                suppressAuth: true,
                suppressVenue: true,
                notifyRequestPending: true,
                notifySuccess: false
            })
            .then(res => {
                commit('setUserInfo', res.data);
                return dispatch('saveUserToken', res.data.token);
            })
            .then(() => {
                return dispatch('getUserScopeOptions');
            });
    },
    refreshToken({ commit, state, dispatch }) {
        console.log('calling /refreshToken for ', state.userToken);
        return http
            .get('refreshToken')
            .then(res => {
                commit('setUserScopeOptions', []);
                commit('setUserInfo', res.data);
                return dispatch('saveUserToken', res.data.token);
            })
            .then(() => {
                return dispatch('getUserScopeOptions');
            });
    },
    getUserInfo({ commit, state, dispatch }) {
        console.log('calling getUserInfo');
        return http
            .get(state.baseUrl, {
                suppressVenue: false,
                notifyRequestPending: true,
                notifyError: true,
                errorMessage:
                    'It look like that your login key is invalid! Please try to login again.'
            })
            .then(res => {
                commit('setUserInfo', res.data);
            })
            .then(() => {
                return dispatch('getUserScopeOptions');
            })
            .catch(() => {
                dispatch('clearUserToken');
            });
    },
    logout({ commit, dispatch }) {
        return http
            .post('logout', null, {
                suppressVenue: true,
                notifyRequestPending: true,
                notifySuccess: false,
                notifyError: false
            })
            .then(() => {
                dispatch('saveUserToken', '');
                dispatch('saveAuthToken', '');
                commit('setUserInfo', {});
                commit('setUserScopeOptions', []);
                dispatch('venue/clearSelectedVenue', null, { root: true });
                commit('venue/setSelectedVenueInfo', {}, { root: true });
                dispatch('buildUserPermissions');
                dispatch('buildDataEntityAbilities', null, { root: true });
            })
            .catch(error => {
                console.log(error.toString());
            });
    },
    forgotPassword(_, { email, url, mso }) {
        // will call the forgotPassword api to send a reset password email
        const data = {
            email,
            url,
            mso
        };
        return http.post('forgotPassword', data, {
            suppressAuth: true,
            suppressVenue: true,
            notifySuccess: true,
            successMessage: 'Password reset request submitted successfully'
        });
    },
    resetPassword({ commit, dispatch, state }, { password, url, mso }) {
        if (state.authToken) {
            const data = {
                password,
                token: state.authToken,
                url,
                mso
            };
            return http.post('resetPassword', data, {
                suppressAuth: true,
                suppressVenue: true,
                notifySuccess: true,
                successMessage: 'User password has been reset successfully'
            });
        } else if (state.userToken) {
            const data = {
                password
            };
            return http
                .patch(state.baseUrl, data, {
                    notifySuccess: true,
                    successMessage: 'User password has been reset successfully'
                })
                .then(res => {
                    commit('setUserInfo', res.data);
                    return dispatch('saveUserToken', res.data.token);
                })
                .then(() => {
                    return dispatch('getUserScopeOptions');
                });
        }
    },
    updateUserProfile({ commit, dispatch, state }, userObject) {
        console.log('calling updateUserProfile');
        return http
            .patch(state.baseUrl, userObject, {
                notifyRequestPending: true,
                notifyError: true,
                successMessage: 'User profile saved successfully.'
            })
            .then(res => {
                commit('setUserInfo', res.data);
                return dispatch('saveUserToken', res.data.token);
            })
            .then(() => {
                return dispatch('getUserScopeOptions');
            });
    },
    getUserScopeOptions({ commit, dispatch, rootGetters }) {
        console.log('calling getUserScopeOptions');
        return http
            .get('userScopeOptions')
            .then(res => {
                commit('setUserScopeOptions', res.data);
            })
            .then(() => {
                if (!rootGetters['venue/currentVenue']) {
                    return dispatch('autoSelectVenue');
                }
            })
            .catch(error => {
                console.log('--- error ', error);
            });
    },
    autoSelectVenue({ state, dispatch }) {
        console.log('calling autoSelectVenue');
        // will select a venue if userScopeOptions have a single venue
        if (state.userScopeOptions.length === 1) {
            return dispatch(
                'venue/saveSelectedVenue',
                state.userScopeOptions[0],
                { root: true }
            );
        }
    },
    buildUserPermissions({ commit, state, rootGetters }) {
        let permissions = [];
        if (
            state.userInfo &&
            state.userInfo.userScopes &&
            rootGetters['venue/currentVenue']
        ) {
            state.userInfo.userScopes.forEach(userScope => {
                if (
                    userScope.venue === rootGetters['venue/currentVenue'] ||
                    userScope.venue === '*'
                ) {
                    userScope.roles.forEach(role => {
                        permissions.push(...role.permissions);
                    });
                }
            });
        }
        if (permissions.length) {
            // remove duplicates
            permissions = [...new Set(permissions)].sort();
        }
        commit('setUserPermissions', permissions);
    },
    clearUserToken({ commit, dispatch }) {
        // will clear the current user because token is not valid
        console.log('calling clearUserToken');
        dispatch('saveUserToken', '');
        commit('setUserInfo', {});
        commit('setUserScopeOptions', []);
    }
};

export default {
    namespaced: true,
    state: defaultState,
    getters,
    mutations,
    actions
};
